import { useRef } from 'react';

import { css, Popper, styled } from '@mui/material';
import { Grid, InfoButton, Typography } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';

import NotificationPreferenceToggle from './NotificationPreferenceToggle';

import type { PopperProps } from '@mui/material';

interface NotificationSettingPopperProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  notificationToggleId?: string;
}

const NotificationSettingPopper = ({
  anchorEl,
  notificationToggleId,
  open,
}: NotificationSettingPopperProps) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement | null>(null);

  return (
    <StyledPopper
      anchorEl={anchorEl}
      open={open}
      placement="top-end"
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
    >
      <NotificationSettingPaper ref={panelRef} data-toggle-id={notificationToggleId}>
        <Grid container alignItems="center" gap={0.5}>
          <Typography variant="label/semibold/xs" color="text/weakest/default">
            {t('layout:notification.setting.title')}
          </Typography>
          <InfoButton
            size="sm"
            tooltipProps={{
              message: t('layout:notification.setting.description'),
            }}
          />
        </Grid>
        <NotificationPreferenceToggle
          type="inAppPush"
          title={t('layout:notification.setting.in_app.label')}
          description={t('layout:notification.setting.in_app.description')}
        />
        <NotificationPreferenceToggle
          type="browserPush"
          title={t('layout:notification.setting.browser.label')}
          description={t('layout:notification.setting.browser.description')}
        />
        <NotificationPreferenceToggle
          title={t('layout:notification.setting.email.label')}
          description={t('layout:notification.setting.email.description')}
          checked={false}
          disabled
        />
      </NotificationSettingPaper>
    </StyledPopper>
  );
};

export default NotificationSettingPopper;

const StyledPopper = styled(Popper)<PopperProps>`
  z-index: 1301;
`;

const NotificationSettingPaper = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 292px;
    padding: 8px 0;
    background-color: ${theme.color['surface/default']};
    box-shadow: ${theme.elevation.level_2};
    overflow: hidden;

    & > :first-of-type {
      padding: 6px 16px;
    }
  `,
);
